/**
 * OpsNotificationsBell — sino de notificações do Ops (P1.5a)
 *
 * Superfícies independentes:
 *  - Sino 🔔 N  = eventos não lidos (ops_notifications)
 *  - Badge "Solicitações de Acesso N" = leads em NEW (domínio comercial)
 * Marcar lida NUNCA altera o status do access request.
 *
 * Refresh: mount + mudança de rota + após marcar lida + polling 60s.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { AZURE } from '../../services/api';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const timeAgo = (iso, e) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return e ? 'now' : 'agora';
  const m = Math.floor(s / 60);
  if (m < 60) return e ? `${m} min ago` : `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return e ? `${h}h ago` : `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return e ? 'yesterday' : 'ontem';
  return e ? `${d} days ago` : `há ${d} dias`;
};

const OpsNotificationsBell = () => {
  const { lang } = useGovernanceLang();
  const e = lang === 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchSummary = useCallback(() => {
    AZURE.get('/governance/notifications/summary')
      .then(({ data }) => { if (data?.success) setUnread(data.data?.unread || 0); })
      .catch(() => {}); // sino é opcional — nunca quebra o layout
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get('/governance/notifications', { params: { limit: 20 } });
      if (data?.success) setItems(data.data || []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);

  // mount + mudança de rota + polling 60s
  useEffect(() => { fetchSummary(); }, [fetchSummary, location.pathname]);
  useEffect(() => {
    const t = setInterval(fetchSummary, 60000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  // fechar ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onClick = (ev) => {
      if (panelRef.current && !panelRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const onItemClick = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      try { await AZURE.patch(`/governance/notifications/${n.notification_id}/read`); }
      catch { /* navegação não depende da marcação */ }
      fetchSummary();
    }
    if (n.target_url) navigate(n.target_url);
  };

  const markAllRead = async () => {
    try {
      await AZURE.patch('/governance/notifications/read-all');
      fetchSummary();
      fetchList();
    } catch { /* silencioso */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggle}
        aria-label={e ? 'Notifications' : 'Notificações'}
        className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800/60 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center
            rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#dc2626' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 lg:right-auto lg:left-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl
          border border-gray-200 z-[60] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-extrabold text-gray-900">{e ? 'Notifications' : 'Notificações'}</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900"
              >
                <CheckCheck size={13} />
                {e ? 'Mark all as read' : 'Marcar todas como lidas'}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">{e ? 'Loading…' : 'Carregando…'}</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Inbox className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{e ? 'No notifications' : 'Sem notificações'}</p>
              </div>
            )}
            {!loading && items.map((n) => {
              const isUnread = !n.read_at;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex gap-2.5
                    ${isUnread ? 'bg-blue-50/40' : ''}`}
                >
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isUnread ? 'bg-blue-600' : 'bg-transparent'}`} />
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm ${isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}`}>
                      {e ? n.title_en : n.title_pt}
                    </span>
                    {(e ? n.body_en : n.body_pt) && (
                      <span className="block text-xs text-gray-500 truncate">{e ? n.body_en : n.body_pt}</span>
                    )}
                    <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at, e)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpsNotificationsBell;
